// 3. Internal components
import { LoadingSpinner } from '~/components/common/LoadingSpinner';

interface FullPageSpinnerProps {
  message?: string;
}

/**
 * Full-page loading spinner displayed during async operations.
 *
 * KNOWN ISSUES - SPINNER ANIMATION (BUG-001, BUG-012, BUG-026):
 *
 * During OAuth redirects and WebSocket connection waiting periods, the spinner
 * may appear frozen or not animate smoothly. This behavior is caused by browser
 * processing during navigation and is not fixable via application code.
 *
 * Affected scenarios:
 * - BUG-001: OAuth redirect flow (browser navigates away, suspending animations)
 * - BUG-012: WebSocket connection wait (browser prioritizes network I/O)
 * - BUG-026: SSO callback processing (redirect in progress)
 *
 * Root cause:
 * Browsers reduce or suspend CSS animations and JavaScript execution during:
 * - Navigation/redirect events (location.href changes)
 * - Heavy network operations (WebSocket handshake)
 * - Page unload/beforeunload lifecycle events
 *
 * This is standard browser behavior for performance optimization and cannot be
 * overridden by application code.
 *
 * Alternative solutions (if this becomes UX blocker):
 * - Static loading indicators (no animation dependency)
 * - Skeleton screens showing expected content structure
 * - Progress messages with increasing detail over time
 * - Animated GIF images (browser-native animation, more resilient)
 *
 * Current decision: Accept browser limitation. Spinner still provides visual
 * feedback that system is working, even if animation pauses temporarily.
 */
export function FullPageSpinner({ message }: FullPageSpinnerProps) {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LoadingSpinner message={message} />
    </div>
  );
}
